import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import endpoints from "../api/endpoints";
import Spinner from "../Components/Spinner";
import {
  DEFAULT_ASSET_CARD_CONFIG,
  getAssetCardDeviceDetailsFieldOptions,
  normalizeAssetCardConfig,
  normalizeStringList,
} from "../utils/assetCardConfig";
import "./AssetDeviceDetailsConfigModal.css";

const normalizeFieldValues = (value) => Array.from(new Set(normalizeStringList(value)));

const sanitizeSelectedFields = (fields, availableFields, maxItems) => {
  const availableSet = new Set(normalizeFieldValues(availableFields));
  const max = Math.max(1, Number(maxItems) || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems);

  return normalizeFieldValues(fields)
    .filter((field) => availableSet.has(field))
    .slice(0, max);
};

const groupFields = (fields) =>
  fields.reduce((groups, field) => {
    const group = field.group || "Other";
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
    return groups;
  }, {});

const AssetDeviceDetailsConfigModal = ({ open, organizationId, onClose, onSaved, onAlert }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [maxItemsLimit, setMaxItemsLimit] = useState(DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems);
  const [availableFields, setAvailableFields] = useState(DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);
  const [policyAdjustment, setPolicyAdjustment] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!open || !organizationId) return;
    setLoading(true);
    try {
      const res = await axios.get(endpoints.configuration.assetCardDeviceDetails(), {
        params: { organization_id: organizationId },
      });
      const nextLimit = Math.max(
        1,
        Number(res.data?.data?.deviceDetailsMaxItemsLimit || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems)
      );
      const nextConfig = normalizeAssetCardConfig(res.data?.data?.assetCard);
      const adminConfiguredFields = normalizeFieldValues(res.data?.data?.deviceDetailsAvailableFields);
      const nextAvailableFields = adminConfiguredFields.length
        ? adminConfiguredFields
        : normalizeFieldValues(nextConfig.deviceDetailsFields);
      const nextSelectedFields = sanitizeSelectedFields(
        nextConfig.deviceDetailsFields,
        nextAvailableFields,
        nextLimit
      );

      setConfig({
        ...nextConfig,
        deviceDetailsMaxItems: Math.min(nextLimit, nextSelectedFields.length || nextConfig.deviceDetailsMaxItems),
        deviceDetailsFields: nextSelectedFields,
      });
      setMaxItemsLimit(nextLimit);
      setPolicyAdjustment(res.data?.data?.deviceDetailsPolicyAdjustment || null);
      setAvailableFields(nextAvailableFields);
    } catch (error) {
      console.error("Failed to load asset display settings", error);
      onAlert?.("error", "Failed to load asset display settings.");
    } finally {
      setLoading(false);
    }
  }, [open, organizationId, onAlert]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const availableFieldValues = useMemo(() => normalizeFieldValues(availableFields), [availableFields]);
  const availableFieldSet = useMemo(() => new Set(availableFieldValues), [availableFieldValues]);
  const maxAllowedItems = Math.max(1, Number(maxItemsLimit) || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems);
  const selectedFields = useMemo(
    () => sanitizeSelectedFields(config.deviceDetailsFields, availableFieldValues, maxAllowedItems),
    [availableFieldValues, config.deviceDetailsFields, maxAllowedItems]
  );
  const selectedFieldCount = selectedFields.length;
  const selectionIsFull = selectedFieldCount >= maxAllowedItems;
  const fieldsToRender = useMemo(
    () => getAssetCardDeviceDetailsFieldOptions(availableFieldValues),
    [availableFieldValues]
  );
  const fieldGroups = useMemo(() => groupFields(fieldsToRender), [fieldsToRender]);
  const hasAvailableFields = fieldsToRender.length > 0;

  const toggleField = (fieldValue) => {
    if (!availableFieldSet.has(fieldValue)) return;

    setConfig((prev) => {
      const current = new Set(sanitizeSelectedFields(prev.deviceDetailsFields, availableFieldValues, maxAllowedItems));
      if (current.has(fieldValue)) {
        current.delete(fieldValue);
      } else if (current.size < maxAllowedItems) {
        current.add(fieldValue);
      }

      return {
        ...prev,
        deviceDetailsMaxItems: Math.min(maxAllowedItems, current.size || prev.deviceDetailsMaxItems),
        deviceDetailsFields: Array.from(current),
      };
    });
  };

  const save = async () => {
    const fieldsToSave = sanitizeSelectedFields(selectedFields, availableFieldValues, maxAllowedItems);
    if (!fieldsToSave.length) return;

    setSaving(true);
    try {
      const res = await axios.put(endpoints.configuration.assetCardDeviceDetails(), {
        organization_id: organizationId,
        assetCard: {
          deviceDetailsMaxItems: fieldsToSave.length,
          deviceDetailsFields: fieldsToSave,
        },
      });
      const nextConfig = normalizeAssetCardConfig(res.data?.data?.assetCard);
      const nextLimit = Math.max(
        1,
        Number(res.data?.data?.deviceDetailsMaxItemsLimit || maxItemsLimit)
      );
      const adminConfiguredFields = normalizeFieldValues(res.data?.data?.deviceDetailsAvailableFields);
      const nextAvailableFields = adminConfiguredFields.length
        ? adminConfiguredFields
        : normalizeFieldValues(nextConfig.deviceDetailsFields);
      const nextSelectedFields = sanitizeSelectedFields(
        nextConfig.deviceDetailsFields,
        nextAvailableFields,
        nextLimit
      );

      setConfig({
        ...nextConfig,
        deviceDetailsMaxItems: Math.min(nextLimit, nextSelectedFields.length || nextConfig.deviceDetailsMaxItems),
        deviceDetailsFields: nextSelectedFields,
      });
      setMaxItemsLimit(nextLimit);
      setAvailableFields(nextAvailableFields);
      setPolicyAdjustment(res.data?.data?.deviceDetailsPolicyAdjustment || null);
      onSaved?.({
        ...nextConfig,
        deviceDetailsMaxItems: nextSelectedFields.length,
        deviceDetailsFields: nextSelectedFields,
      });
      onAlert?.("success", "Device details asset display updated.");
      onClose?.();
    } catch (error) {
      console.error("Failed to save asset display settings", error);
      onAlert?.("error", "Failed to save asset display settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="asset-summary-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="asset-summary-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Device details asset display"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="asset-summary-modal-header">
          <div>
            <h2>Device details asset display</h2>
            <p>Choose what asset information appears in the Device Details header.</p>
          </div>
          <button type="button" className="asset-summary-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="asset-summary-modal-body">
          {loading ? (
            <div className="asset-summary-modal-loading">
              <Spinner />
            </div>
          ) : (
            <>
              {policyAdjustment?.required ? (
                <div className="asset-summary-policy-warning" role="alert">
                  <strong>Administration changed the field selection rules.</strong>
                  <span>
                    Your previous field selection no longer fits the current administration rules.
                    Until you save this update, Device Details shows only allowed fields from your choices.
                  </span>
                </div>
              ) : null}

              <div className="asset-summary-control">
                <div>
                  <strong>Display fields</strong>
                  <small>
                    Only fields enabled by Super in System Configurations are shown here.
                    Choose up to {maxAllowedItems} fields.
                  </small>
                </div>
                <div className={`asset-summary-selection-count ${selectionIsFull ? "is-full" : ""}`}>
                  <strong>{selectedFieldCount}</strong>
                  <span>/ {maxAllowedItems} selected</span>
                </div>
              </div>

              {hasAvailableFields ? (
                <div className="asset-summary-fields-wrap">
                  {Object.entries(fieldGroups).map(([group, fields]) => (
                    <div className="asset-summary-field-group" key={group}>
                      <div className="asset-summary-field-group-title">{group}</div>
                      <div className="asset-summary-fields">
                        {fields.map((field) => {
                          const checked = selectedFields.includes(field.value);
                          const disabled = !checked && selectionIsFull;
                          return (
                            <label
                              className={`asset-summary-field ${checked ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                              key={field.value}
                              title={disabled ? `You can select up to ${maxAllowedItems} fields.` : field.value}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleField(field.value)}
                              />
                              <span>{field.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="asset-summary-empty" role="status">
                  <strong>No fields enabled</strong>
                  <span>Enable Device Details fields from System Configurations &gt; Asset Cards first.</span>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="asset-summary-modal-footer">
          <button type="button" className="asset-summary-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="asset-summary-primary"
            onClick={save}
            disabled={loading || saving || !hasAvailableFields || selectedFieldCount === 0}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AssetDeviceDetailsConfigModal;
